import { BrickColor } from '../../../components/brickColor/BrickColor';
import { ONE_OFF_EVENT_TYPES } from '../../../entities/events/shared';
import { readableDate } from '@chocolate-soup-inc/cs-api-consumer-utils';
import { ErrorPage, Icon, InfoCardInner, LoadingPage, TMenuItemProps, Tooltip } from '@chocolate-soup-inc/cs-frontend-components';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AlertTooltipDismiss } from '../../../components/alerts/AlertTooltipDismiss';
import { useFilter } from '../../../contexts/filters';
import { getReadableRelationshipType } from '../../../entities/dependants/shared';
import { TGiftType } from '../../../entities/gifts/queries';
import { getReadableGiftType, getRemovedInfoFromRecipientData, isDonationGift } from '../../../entities/gifts/shared';
import { isDependant } from '../../../entities/recipients/shared';
import { useAuthContextUserEmail } from '../../../entities/shared/auth';
import { useQueryOneShipment } from '../../../entities/shipments/queries';
import { TShipmentStatuses, useUpdateGiftMutation } from '../../../generated/graphql';
import { MoveGiftFromShipmetModal } from './MoveGiftFromShipmetModal';
import { ShipmentModal } from './ShipmentModal';
import style from './ShipmentModalGifts.module.scss';

export const ShipmentModalGifts = () => {
  const { filtersModalMode } = useFilter();
  useEffect(() => {
    filtersModalMode();
  }, []); // eslint-disable-line

  const { shipmentId = '0' } = useParams();
  const navigate = useNavigate();

  const userEmail = useAuthContextUserEmail();
  const [updateGift] = useUpdateGiftMutation();

  const modalRef = useRef<HTMLDivElement>(null);

  const [removingGiftFromShipment, setRemovingGiftFromShipment] = useState<TGiftType>();

  const {
    data: shipment,
    error: shipmentError,
    loading: shipmentLoading
  } = useQueryOneShipment({
    id: shipmentId
  });
  //console.log('shipment?:', shipment);

  const gifts = useMemo(() => (shipment.gifts?.items || []) as TGiftType[], [shipment]);
  /*const {
    data: gifts,
    error: giftsError,
    loading: giftsLoading,
  } = useQueryOneShipmentGifts({
    shipmentId,
  });*/
  //console.log('gifts?:', gifts);

  const optionsMenu = useCallback(
    (gift: TGiftType) => {
      if (shipment != null && [TShipmentStatuses.Packaging, TShipmentStatuses.ReadyToShip].includes(shipment.status))
        return [
          {
            closeOnClick: true,
            label: 'Move or Remove gift',
            leadingIcon: 'open_in_new',
            onClick: () => {
              setRemovingGiftFromShipment(gift);
            },
            type: 'text'
          }
        ] as TMenuItemProps[];
    },
    [shipment]
  );

  const closeModal = useCallback(() => {
    setRemovingGiftFromShipment(undefined);
  }, []);

  const onMoveToDoneCancel = useCallback(() => {
    closeModal();
  }, [closeModal]);

  const onMoveToDoneConfirm = useCallback(() => {
    if (gifts.length === 1 && shipment != null) navigate(generatePath('../../'));

    closeModal();
  }, [closeModal, gifts.length, navigate, shipment]);

  if (shipmentLoading)
    return (
      <ShipmentModal headline='Shipment Gifts'>
        <LoadingPage />
      </ShipmentModal>
    );

  if (shipmentError) {
    console.error('query Apollo ERROR:', shipmentError);
    toast.error('An error has ocurred, check the console for details. (query Apollo ERROR)');
    //if (shipmentError.message) toast.error(shipmentError.message);
    if (!shipment) return <ErrorPage error={shipmentError} />;
  }

  return (
    <>
      {removingGiftFromShipment != null && (
        <div ref={modalRef}>
          <MoveGiftFromShipmetModal
            addressId={removingGiftFromShipment.addressId as string}
            companyId={removingGiftFromShipment.companyId}
            id={removingGiftFromShipment.id}
            shipment={shipment}
            giftsLength={gifts.length}
            closeModal={closeModal}
            onCancel={onMoveToDoneCancel}
            onSuccess={onMoveToDoneConfirm}
          />
        </div>
      )}
      <ShipmentModal headline='Shipment Gifts'>
        {gifts.map((gift, index) => {
          return (
            <InfoCardInner<TGiftType>
              className={clsx(!gift.recipient && style.deleted)}
              key={gift.id}
              data={gift}
              sections={[
                {
                  title: `Gift #${index + 1}`,
                  columns: [
                    {
                      label: 'Recipient Name',
                      render: () => {
                        return (
                          <div className={clsx(style.badgesContainer)}>
                            <span>{gift.recipient?.fullName ?? getRemovedInfoFromRecipientData(gift)}</span>
                            <AlertTooltipDismiss entity={gift} userEmail={userEmail} updateMutation={updateGift} />
                          </div>
                        );
                      }
                    },
                    {
                      label: 'Event Type',
                      render: () => getReadableGiftType(gift)
                    },
                    {
                      label: 'Event Date',
                      render: () => readableDate(gift.eventDate)
                    },
                    {
                      label: 'Event Notes',
                      render: () => gift.notes
                    },
                    {
                      label: 'Relationship',
                      render: () => {
                        const recipient = gift.recipient;
                        if (recipient && isDependant(recipient)) return `${getReadableRelationshipType(recipient)} of ${recipient.employee?.fullName}`;
                      }
                    },
                    {
                      label: 'Donation',
                      render: () => (isDonationGift({ gift }) ? 'yes' : 'no')
                    },
                    ...(gift.oneOffSource || gift.type in ONE_OFF_EVENT_TYPES || gift.brickColor || gift.brickSize || gift.brickQuantity || gift.brickText
                      ? [
                          {
                            label: 'Brick Details',
                            render: () => (
                              <div className={style.brickDetails}>
                                <span className={style.brickDetail}>
                                  <BrickColor value={gift.brickColor} />
                                </span>
                                <span className={style.brickDetail} aria-label={`Brick Size: ${gift.brickSize || '-'}`}>
                                  <Tooltip message='Brick Size'>
                                    <Icon icon='straighten' />
                                  </Tooltip>
                                  {gift.brickSize || '-'}
                                </span>
                                <span className={style.brickDetail} aria-label={`Brick Quantity: ${gift.brickQuantity ?? 1}`}>
                                  <Tooltip message='Brick Quantity'>
                                    <Icon icon='inventory_2' />
                                  </Tooltip>
                                  {gift.brickQuantity ?? 1} pc(s)
                                </span>
                                <br />
                                <span className={style.brickDetail} aria-label={`Brick Text: ${gift.brickText}`}>
                                  <Tooltip message='Printed Text'>
                                    <Icon icon='print' />
                                  </Tooltip>
                                  {gift.brickText}
                                </span>
                              </div>
                            )
                          }
                        ]
                      : [])
                  ],
                  optionsMenu: optionsMenu(gift)
                }
              ]}
            />
          );
        })}
      </ShipmentModal>
    </>
  );
};
